import { Route, Routes, Navigate } from "react-router-dom";

import { useAuthActions, useAuthContext } from "@contexts/auth";
import AuthProvider from "@contexts/auth";
import Helmet from "@components/Helmet";
import ReleaseUpdateNotice from "@components/ReleaseUpdateNotice";
import ScrollToTop from "@components/ScrollToTop";

import Access from "@containers/Access";
import Login from "@outlets/access/Login";
import PasswordResetRequest from "@outlets/access/PasswordResetRequest";
import PasswordCreation from "@outlets/access/PasswordCreation";
import CodeValidation from "@outlets/access/CodeValidation";
import Dashboard from "@containers/Dashboard";
import Details from "@outlets/dashboard/Details";
import Value from "@outlets/dashboard/Value";
import Projection from "@outlets/dashboard/Projection";
import AppContainer from "@containers/AppContainer";
import DashboardContainer from "@containers/DashboardContainer";

import Users from "@outlets/backoffice/users";
import Curve from "@outlets/dashboard/Curve";
import Costs from "@outlets/dashboard/Costs";
import Advancements from "@outlets/dashboard/Advancements";
import Entry from "@outlets/costs/Entry";
import Params from "@outlets/costs/Params";
import Items from "@outlets/costs/Items";
import MeasurementUnits from "@outlets/costs/MeasurementUnits";
import Indexers from "@outlets/costs/Indexers";
import Comparison from "@outlets/costs/Comparison";
import Creditor from "@outlets/costs/Creditor";

import Indexes from "@outlets/costs/Indexes";
import Project from "@outlets/bim/Project";
import Checklist from "@outlets/bim/Checklist";
import Classification from "@outlets/bim/Classification";
import Lessons from "@outlets/bim/Lessons";
import Options from "@outlets/bim/Options";
import Deliverables from "@outlets/bim/Deliverables";
import Summary from "@outlets/costs/Summary";
import UnitValues from "@outlets/costs/UnitValues";
import { useEffect, useState } from "react";
import Timeline from "@outlets/bim/Timeline";
import ExtraValues from "@outlets/dashboard/ExtraValues";
import ItemsTimeline from "@outlets/dashboard/Timeline";
import Settings from "@outlets/configs/Settings";
import MainRegister from "@outlets/registerForm/mainRegister";
import CartesianProvider from "@contexts/cartesian";
import ClientForm from "@outlets/registerForm/clientForm";
import CartesianForms from "@outlets/backoffice/cartesianForms";
import DashLessons from "@outlets/dashboard/Lessons";
import Cubs from "@outlets/backoffice/indexers";
import SupplierExplorer from "@outlets/backoffice/SupplierExplorer";
import Audit from "@outlets/backoffice/Audit";
import Integrations from "@outlets/backoffice/Integrations";
import SiengePermissions from "@outlets/backoffice/SiengePermissions";
import ProductAccess from "@outlets/backoffice/ProductAccess";
import FinancialGovernance from "@outlets/backoffice/FinancialGovernance";
import CompanyBuildingDirectory from "@outlets/backoffice/companyBuildingDirectory";
import DirectorRoute from "@components/director/DirectorRoute";
import DirectorEntry from "@outlets/director/DirectorEntry";
import DirectorOverview from "@outlets/director/ExecutiveOverview";
import DirectorCashFlow from "@outlets/director/CashFlow";
import DirectorAnalyses from "@outlets/director/Analyses";
import LiquidityView from "@outlets/director/LiquidityView";
import ManagerialDre from "@outlets/director/ManagerialDre";
import DirectorReturns from "@outlets/director/Returns";
import DirectorScenarios from "@outlets/director/Scenarios";
import DataGovernance from "@outlets/director/DataGovernance";
import CentersTitles from "@outlets/director/CentersTitles";
import CustomerReceipts from "@outlets/director/CustomerReceipts";
import CustomerReceiptDetail from "@outlets/director/CustomerReceiptDetail";
import CostPlanning from "@outlets/director/CostPlanning";
import OrdersAndContracts from "@outlets/dashboard/OrdersAndContracts";
import {
  dashboardPaths,
  resolveDashboardPathPermission,
} from "@outlets/configs/options";

const App = () => {

  const PrivateRoute = ({ element, roleNeeded, checkPermission }) => {
    const { user } = useAuthContext();
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      if (user !== undefined) {
        setLoading(false);
      } else {

      }
    }, [user]);

    if (loading) {
      return <div>Carregando...</div>;
    }

    if (!user) {
      return <Navigate to="/access" />;
    }

    if ((roleNeeded && !user.roles?.includes(roleNeeded)) || (checkPermission && !user?.permissions?.pageViews?.includes(checkPermission))) {
      return <Navigate to="/dashboard" />;
    }

    return element;
  };


  return (
    <AuthProvider>
      <ScrollToTop>
        <Helmet />
        <ReleaseUpdateNotice />
        <Routes>
          <Route path="/" element={<AppContainer />}>
            <Route path="/access" element={<Access />}>
              <Route index element={<Login />} />
              <Route path="recuperar-senha" element={<PasswordResetRequest />} />
              <Route path="definir-senha" element={<PasswordCreation />} />
              <Route path="codigo-verificacao" element={<CodeValidation />} />
            </Route>
            <Route path="/dashboard" element={<DashboardContainer />}>
              <Route path=":project_id?" element={<PrivateRoute roleNeeded={'ampli'} element={<Details />} />} />
              {dashboardPaths.map((item) => (
                <Route key={item.path} path={`${item.path}/:project_id?`} element={<PrivateRoute roleNeeded={'ampli'} element={item.element} checkPermission={resolveDashboardPathPermission(item)} />} />
              ))}
              {/* <Route path="valor-agregado/:project_id?" element={<PrivateRoute roleNeeded={'ampli'} element={<Value />} checkPermission="Valor agregado" />} />
              <Route path="projecao-financeira/:project_id?" element={<PrivateRoute roleNeeded={'ampli'} element={<Projection />} checkPermission="Projeção financeira" />} />
              <Route path="curva/:project_id?" element={<PrivateRoute roleNeeded={'ampli'} element={<Curve />} checkPermission="Curva ABC" />} />
              <Route path="adiantamentos/:project_id?" element={<PrivateRoute roleNeeded={'ampli'} element={<Advancements />} checkPermission="Adiantamentos" />} />
              <Route path="valores/:project_id?" element={<PrivateRoute roleNeeded={'ampli'} element={<ExtraValues />} checkPermission="Valores não lançados" />} />
              <Route path="titulos/:project_id?" element={<PrivateRoute roleNeeded={'ampli'} element={<ItemsTimeline />} checkPermission="Títulos" />} />
              <Route path="licoes/:project_id?" element={<PrivateRoute roleNeeded={'ampli'} element={<DashLessons />} checkPermission="Lições aprendidas" />} />
              <Route path="pedidoscontratos/:project_id?" element={<PrivateRoute roleNeeded={'ampli'} element={<OrdersAndContracts />} checkPermission="Pedidos e Contratos" />} /> */}
              {/* New user / New Company / New Building deixaram de ter rota própria — agora
                  vivem como botões + Modal dentro de "Usuários" e "Construtoras e Obras". */}
              <Route path="backoffice/users/:project_id?" element={<PrivateRoute roleNeeded={'admin'} element={<Users />} />} />
              <Route path="backoffice/companies-buildings" element={<PrivateRoute roleNeeded={'admin'} element={<CompanyBuildingDirectory />} />} />
              <Route path="backoffice/indexers" element={<PrivateRoute roleNeeded={'admin'} element={<Cubs />} />} />
              <Route path="backoffice/cartesian-forms" element={<PrivateRoute roleNeeded={'admin'} element={<CartesianForms />} />} />
              <Route path="backoffice/supplier-explorer" element={<PrivateRoute roleNeeded={'admin'} element={<SupplierExplorer />} />} />
              <Route path="backoffice/audit" element={<PrivateRoute roleNeeded={'admin'} element={<Audit />} />} />
              <Route path="backoffice/integrations" element={<PrivateRoute roleNeeded={'admin'} element={<Integrations />} />} />
              <Route path="backoffice/sienge-permissions" element={<PrivateRoute roleNeeded={'admin'} element={<SiengePermissions />} />} />
              <Route path="backoffice/product-access" element={<PrivateRoute roleNeeded={'admin'} element={<ProductAccess />} />} />
              <Route path="backoffice/financial-governance" element={<PrivateRoute roleNeeded={'admin'} element={<FinancialGovernance />} />} />
              <Route path="director" element={<PrivateRoute element={<DirectorRoute><DirectorEntry /></DirectorRoute>} />} />
              <Route path="director/cost-planning" element={<PrivateRoute element={<DirectorRoute capability="director.cost_plan.read"><CostPlanning /></DirectorRoute>} />} />
              <Route path="director/overview" element={<PrivateRoute element={<DirectorRoute><DirectorOverview /></DirectorRoute>} />} />
              <Route path="director/cashflow" element={<PrivateRoute element={<DirectorRoute capability="director.cashflow.read"><DirectorCashFlow /></DirectorRoute>} />} />
              <Route path="director/liquidity/weekly" element={<PrivateRoute element={<DirectorRoute capability="director.cashflow.read"><LiquidityView view="weekly" /></DirectorRoute>} />} />
              <Route path="director/liquidity/critical-periods" element={<PrivateRoute element={<DirectorRoute capability="director.liquidity.read"><LiquidityView view="critical" /></DirectorRoute>} />} />
              <Route path="director/liquidity/causes" element={<PrivateRoute element={<DirectorRoute capability="director.cashflow.read"><LiquidityView view="causes" /></DirectorRoute>} />} />
              <Route path="director/dre" element={<PrivateRoute element={<DirectorRoute capability="director.dre.managerial.read"><ManagerialDre /></DirectorRoute>} />} />
              <Route path="director/returns" element={<PrivateRoute element={<DirectorRoute capability="director.return.read"><DirectorReturns /></DirectorRoute>} />} />
              <Route path="director/returns/details" element={<PrivateRoute element={<DirectorRoute capability="director.return.read"><DirectorReturns detail /></DirectorRoute>} />} />
              <Route path="director/centers" element={<PrivateRoute element={<DirectorRoute capability="director.portfolio.read"><CentersTitles /></DirectorRoute>} />} />
              <Route path="director/titles" element={<PrivateRoute element={<DirectorRoute capability="receivables.details.read"><CentersTitles titles /></DirectorRoute>} />} />
              <Route path="director/scenarios" element={<PrivateRoute element={<DirectorRoute capability="director.scenarios.read"><DirectorScenarios /></DirectorRoute>} />} />
              <Route path="director/governance" element={<PrivateRoute element={<DirectorRoute capability="director.governance.read"><DataGovernance /></DirectorRoute>} />} />
              <Route path="director/governance/portfolio" element={<PrivateRoute element={<DirectorRoute capability="director.governance.read"><DataGovernance portfolioOnly /></DirectorRoute>} />} />
              <Route path="director/analyses" element={<PrivateRoute element={<DirectorRoute capability="director.portfolio.read"><DirectorAnalyses /></DirectorRoute>} />} />
              <Route path="director/customer-receipts" element={
                <PrivateRoute element={<DirectorRoute capability="receivables.summary.read"><CustomerReceipts /></DirectorRoute>} />
              } />
              <Route path="director/customer-receipts/:customer_ref" element={
                <PrivateRoute element={<DirectorRoute capability="receivables.details.read"><CustomerReceiptDetail /></DirectorRoute>} />
              } />
              <Route path="costs/obras" element={<PrivateRoute roleNeeded={'costs'} element={<Entry />} />} />
              <Route path="costs/parametros" element={<PrivateRoute roleNeeded={'costs'} element={<Params />} />} />
              <Route path="costs/unidades" element={<PrivateRoute roleNeeded={'costs'} element={<MeasurementUnits />} />} />
              <Route path="costs/indices" element={<PrivateRoute roleNeeded={'costs'} element={<Indexes />} />} />
              <Route path="costs/precos" element={<PrivateRoute roleNeeded={'costs'} element={<UnitValues />} />} />
              <Route path="costs/itens" element={<PrivateRoute roleNeeded={'costs'} element={<Items />} />} />
              <Route path="costs/cub" element={<PrivateRoute roleNeeded={'costs'} element={<Indexers />} />} />
              <Route path="costs/resumos" element={<PrivateRoute roleNeeded={'costs'} element={<Summary />} />} />
              <Route path="costs/comparacao" element={<PrivateRoute roleNeeded={'costs'} element={<Comparison />} />} />
              <Route path="costs/fornecedores" element={<PrivateRoute roleNeeded={'costs'} element={<Creditor />} />} />
              <Route path="bim/cadastro" element={<PrivateRoute roleNeeded={'bim'} element={<Project />} />} />
              <Route path="bim/entregaveis" element={<PrivateRoute roleNeeded={'bim'} element={<Deliverables />} />} />
              <Route path="bim/checklist" element={<PrivateRoute roleNeeded={'bim'} element={<Checklist />} />} />
              <Route path="bim/cronograma" element={<PrivateRoute roleNeeded={'bim'} element={<Timeline />} />} />
              <Route path="bim/classificacao" element={<PrivateRoute roleNeeded={'bim'} element={<Classification />} />} />
              <Route path="bim/opcoes" element={<PrivateRoute roleNeeded={'bim'} element={<Options />} />} />
              <Route path="bim/licoes" element={<PrivateRoute roleNeeded={'bim'} element={<Lessons />} />} />
              <Route path="settings" element={<PrivateRoute roleNeeded={'ampli'} element={<Settings />} />} />
            </Route>
            <Route path="registerForm" element={<MainRegister />} />
            <Route path="clientForm/:project_id?" element={<ClientForm />} />
          </Route>
        </Routes>
      </ScrollToTop>
    </AuthProvider>
  );
};

export default App;

